import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { useStateContext } from '../contexts/ContextProvider';
import { useSubscription } from '../contexts/SubscriptionContext';
import { userRequest } from '../utils/requestMethods';
import { Header } from '../components';
import { displaySchoolTerm } from '../utils/schoolYearUtils';
import { FiCheckCircle, FiAlertTriangle, FiAlertCircle, FiAlertOctagon, FiClock, FiCreditCard, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';

const statusConfig = {
  active: {
    label: 'Active',
    bg: 'bg-green-100',
    text: 'text-green-800',
    icon: <FiCheckCircle className="w-4 h-4" />,
    headerBg: 'bg-gradient-to-r from-[#3F3DA5] to-[#3371E8]',
  },
  pending: {
    label: 'Pending',
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    icon: <FiClock className="w-4 h-4" />,
    headerBg: 'bg-gradient-to-r from-[#3F3DA5] to-[#3371E8]',
  },
  grace: {
    label: 'Grace Period',
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    icon: <FiAlertTriangle className="w-4 h-4" />,
    headerBg: 'bg-gradient-to-r from-[#3F3DA5] to-[#3371E8]',
  },
  restricted: {
    label: 'Restricted',
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    icon: <FiAlertCircle className="w-4 h-4" />,
    headerBg: 'bg-gradient-to-r from-[#3F3DA5] to-[#3371E8]',
  },
  suspended: {
    label: 'Suspended',
    bg: 'bg-red-100',
    text: 'text-red-800',
    icon: <FiAlertOctagon className="w-4 h-4" />,
    headerBg: 'bg-gradient-to-r from-[#3F3DA5] to-[#3371E8]',
  },
};

const SectionCard = ({ title, icon, children, className = '' }) => (
  <div className={`rounded-2xl border border-gray-200 shadow-sm overflow-hidden ${className}`}>
    {title && (
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
        {icon && <span className="text-indigo-600">{icon}</span>}
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

const InstallmentBadge = ({ status }) => (
  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${
    status === 'paid' ? 'bg-green-100 text-green-700'
    : status === 'overdue' ? 'bg-red-100 text-red-700'
    : 'bg-gray-100 text-gray-600'
  }`}>
    {status}
  </span>
);

const InstallmentTable = ({ installments, totalInstallments, highlightNumber, payoffRemaining }) => (
  <div className="rounded-xl border border-gray-200 overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
        <tr>
          <th className="px-4 py-2.5 text-left font-medium">Installment</th>
          <th className="px-4 py-2.5 text-left font-medium">Amount</th>
          <th className="px-4 py-2.5 text-left font-medium hidden sm:table-cell">Due Date</th>
          <th className="px-4 py-2.5 text-left font-medium">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {installments.map((inst) => (
          <tr
            key={inst._id}
            className={inst.installment_number === highlightNumber && !payoffRemaining ? 'bg-indigo-50' : ''}
          >
            <td className="px-4 py-3 font-medium text-gray-800">{inst.installment_number} of {totalInstallments}</td>
            <td className="px-4 py-3 text-gray-800">NGN {Number(inst.amount).toLocaleString()}</td>
            <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">
              {inst.due_date ? new Date(inst.due_date).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
            </td>
            <td className="px-4 py-3">
              <InstallmentBadge status={inst.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Billing = () => {
  const { school } = useStateContext();
  const {
    subscription,
    access,
    loading,
    subscriptionStatus,
    endpointsUnavailable,
    canManageBilling,
    refreshSubscription,
  } = useSubscription();
  const location = useLocation();
  const initialPlan = new URLSearchParams(location.search).get('plan') === 'installment' ? 'installment' : 'full';

  const [payLoading, setPayLoading] = useState(false);
  const [useSavedCard, setUseSavedCard] = useState(false);
  const [paymentPlanType, setPaymentPlanType] = useState(initialPlan);
  const [payoffRemaining, setPayoffRemaining] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  // Subscription history
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);

  const schoolId = school?._id;

  const fetchHistory = useCallback(async (page = 1) => {
    if (!schoolId) return;
    setHistoryLoading(true);
    try {
      const { data } = await userRequest.get(`/subscriptions/history/${schoolId}?page=${page}&limit=10`);
      setHistory(data.subscriptions || []);
      setHistoryPage(data.page || 1);
      setHistoryTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch subscription history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  const fetchPaymentPlan = useCallback(async () => {
    if (!subscription?._id) return;
    setPlanLoading(true);
    try {
      const { data } = await userRequest.get(`/subscriptions/${subscription._id}/payment-plan`);
      setPaymentPlan(data);
    } catch (err) {
      console.error('Failed to fetch payment plan:', err);
    } finally {
      setPlanLoading(false);
    }
  }, [subscription?._id]);

  useEffect(() => {
    if (subscription?._id) {
      fetchPaymentPlan();
    }
  }, [subscription?._id, fetchPaymentPlan]);

  useEffect(() => {
    if (subscription?.payment_plan?.type === 'installment') {
      setPaymentPlanType('installment');
    }
  }, [subscription?.payment_plan?.type]);

  // Derived values
  const savedAuth = subscription?.authorization || school?.subscription?.authorization;
  const hasSavedCard = savedAuth?.reusable === true;
  const status = statusConfig[subscriptionStatus] || statusConfig.pending;
  const balanceDue = subscription
    ? Math.max(0, (subscription.amount || 0) - (subscription.total_paid || 0))
    : 0;
  const hasPartialPayment = (subscription?.total_paid || 0) > 0 && !subscription?.is_fully_paid;
  const embeddedPlan = subscription?.payment_plan;
  const planAlreadySet = embeddedPlan?.type === 'installment';
  const latestSupplementaryCharge = (() => {
    const pending = subscription?.supplementary_charges?.filter(c => c.status === 'pending') || [];
    return pending.length > 0 ? pending[pending.length - 1] : null;
  })();
  const hasPendingInstallments = embeddedPlan?.installments?.some(
    i => i.status === 'pending' || i.status === 'overdue'
  );
  const nextInstallment = paymentPlan?.next_installment
    ?? embeddedPlan?.installments?.find(i => i.status === 'pending' || i.status === 'overdue');
  const nextInstallmentNumber = nextInstallment?.installment_number ?? 1;
  const nextInstallmentAmount = nextInstallment?.amount ?? embeddedPlan?.installment_amount;
  const buttonAmount = (() => {
    if (paymentPlanType === 'installment') {
      if (payoffRemaining) return paymentPlan?.remaining_amount ?? balanceDue;
      return nextInstallmentAmount ?? balanceDue;
    }
    return balanceDue;
  })();

  const handlePay = async () => {
    if (!subscription?._id) {
      toast.error('No subscription found');
      return;
    }
    setPayLoading(true);
    try {
      const callbackUrl = `${window.location.origin}/billing/success?subscriptionId=${subscription._id}`;
      const { data } = await userRequest.post(`/subscriptions/${subscription._id}/pay`, {
        callback_url: callbackUrl,
        use_saved_card: useSavedCard && hasSavedCard,
        payment_plan_type: paymentPlanType,
        installment_number: paymentPlanType === 'installment' ? nextInstallmentNumber : 1,
        payoff_remaining: paymentPlanType === 'installment' ? payoffRemaining : false,
      });

      if (data.charged_directly) {
        toast.success('Payment successful! Your subscription is now active.');
        await refreshSubscription();
      } else if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error('Unexpected response from payment service');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment initiation failed. Please try again.');
    } finally {
      setPayLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl">
      <Header category="Billing" title="Subscription & Payment" />

      {/* ── Subscription Status Card ─────────────────────────────────── */}
      {subscription ? (
        <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm mb-6">

          {/* Gradient header */}
          <div className={`px-6 pt-6 pb-5 ${status.headerBg}`}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/20 text-white mb-3">
                  {status.icon}
                  {status.label}
                </span>
                <h3 className="text-xl font-bold text-white">
                  {displaySchoolTerm(subscription.term)} — {subscription.session}
                </h3>
                {subscription.billing_date && (
                  <p className="text-sm text-white/70 mt-1">
                    Due {new Date(subscription.billing_date).toLocaleDateString('en-NG', { month: 'long', day: 'numeric', year: 'numeric' })}
                    {(subscription.days_overdue > 0 || access?.daysOverdue > 0) && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs font-semibold text-white">
                        {subscription.days_overdue || access?.daysOverdue} days overdue
                      </span>
                    )}
                  </p>
                )}
              </div>
              <div className="text-left sm:text-right">
                <p className="text-xs text-white/60 mb-0.5">Subscription total</p>
                <p className="text-3xl font-bold text-white">NGN {Number(subscription.amount).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-2 sm:grid-cols-3 bg-[#EEEEFC] divide-x divide-indigo-100">
            <div className="px-5 py-4">
              <p className="text-xs text-gray-500 mb-0.5">Total Paid</p>
              <p className="text-lg font-bold text-green-600">NGN {Number(subscription.total_paid || 0).toLocaleString()}</p>
            </div>
            <div className="px-5 py-4">
              <p className="text-xs text-gray-500 mb-0.5">Balance Due</p>
              <p className={`text-lg font-bold ${balanceDue > 0 ? 'text-red-500' : 'text-green-600'}`}>
                NGN {Number(balanceDue).toLocaleString()}
              </p>
            </div>
            <div className="px-5 py-4 hidden sm:block">
              <p className="text-xs text-gray-500 mb-0.5">
                {embeddedPlan?.type === 'installment' ? 'Installment Progress' : 'Billing Cycle'}
              </p>
              {embeddedPlan?.type === 'installment' ? (
                <p className="text-sm font-bold text-indigo-700">
                  {embeddedPlan.installments.filter(i => i.status === 'paid').length} of {embeddedPlan.total_installments} paid
                </p>
              ) : (
                <p className="text-sm font-semibold text-gray-700 capitalize">{subscription.billing_cycle || '—'}</p>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="p-6 bg-white space-y-5">

            {/* Invoice breakdown */}
            {(subscription.active_student_count > 0 || subscription.sms_fee_per_student > 0) && (
              <div className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-100 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Invoice Breakdown</p>
                </div>
                <div className="divide-y divide-gray-100">
                  <div className="flex justify-between items-center px-4 py-3 text-sm">
                    <span className="text-gray-500">Active Students</span>
                    <span className="font-semibold text-gray-800">{subscription.active_student_count}</span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3 text-sm">
                    <span className="text-gray-500">SMS Fee per Student</span>
                    <span className="font-semibold text-gray-800">NGN {Number(subscription.sms_fee_per_student).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3 text-sm bg-white">
                    <span className="font-semibold text-gray-700">Total</span>
                    <span className="font-bold text-indigo-700">NGN {Number(subscription.amount).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Access message */}
            {access?.message && subscriptionStatus !== 'active' && (
              <div className="flex gap-2.5 items-start bg-blue-50 border border-blue-100 rounded-xl p-3 text-sm text-blue-800">
                <FiAlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>{access.message}</p>
              </div>
            )}

            {/* Supplementary Charges */}
            {latestSupplementaryCharge && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Supplementary Charge</h4>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                    pending
                  </span>
                </div>
                <div className="flex items-center justify-between bg-orange-50 border border-orange-100 rounded-xl px-4 py-3 text-sm">
                  <div>
                    <p className="font-semibold text-gray-800">NGN {Number(latestSupplementaryCharge.amount).toLocaleString()}</p>
                    {latestSupplementaryCharge.additional_students > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        +{latestSupplementaryCharge.additional_students} student{latestSupplementaryCharge.additional_students !== 1 ? 's' : ''} added mid-term
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {new Date(latestSupplementaryCharge.created_at).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 capitalize">
                    {latestSupplementaryCharge.status}
                  </span>
                </div>
              </div>
            )}

            {/* Payment Plan */}
            {embeddedPlan && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Payment Plan</h4>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${embeddedPlan.type === 'installment' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                    {embeddedPlan.type === 'installment' ? `Installment · ${embeddedPlan.total_installments} months` : 'Full Payment'}
                  </span>
                </div>

                {embeddedPlan.type === 'installment' && (
                  <>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 bg-gray-50 rounded-xl px-4 py-3 mb-3">
                      <span>Original: <span className="font-semibold text-gray-700">NGN {Number(embeddedPlan.original_amount).toLocaleString()}</span></span>
                      <span className="text-gray-300">·</span>
                      <span>Total financed: <span className="font-semibold text-gray-700">NGN {Number(embeddedPlan.financed_amount).toLocaleString()}</span></span>
                      <span className="text-gray-300">·</span>
                      <span>Financing: <span className="font-semibold text-amber-600">{Math.round(embeddedPlan.financing_rate * 100)}%</span></span>
                    </div>
                    <InstallmentTable
                      installments={embeddedPlan.installments}
                      totalInstallments={embeddedPlan.total_installments}
                      highlightNumber={nextInstallment?.installment_number}
                      payoffRemaining={payoffRemaining}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-200 rounded-2xl p-10 mb-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <FiCreditCard className="w-8 h-8 text-gray-400" />
          </div>
          {endpointsUnavailable ? (
            <>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">Billing Coming Soon</h3>
              <p className="text-sm text-gray-500 max-w-xs mx-auto">
                Subscription management is not yet available. Check back soon or contact{' '}
                <span className="font-medium text-indigo-600">Acceede support</span>.
              </p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">No Active Subscription</h3>
              <p className="text-sm text-gray-500 max-w-xs mx-auto">
                There is no subscription configured for the current term. Contact{' '}
                <span className="font-medium text-indigo-600">Acceede support</span> to get started.
              </p>
            </>
          )}
        </div>
      )}

      {/* ── Make Payment ─────────────────────────────────────────────── */}
      {subscription && (subscriptionStatus !== 'active' || hasPendingInstallments) && (
        <SectionCard
          title={planAlreadySet ? 'Next Payment Due' : 'Make Payment'}
          icon={<FiCreditCard className="w-4 h-4" />}
          className="mb-6"
        >
          {canManageBilling ? (
            <div>
              {/* Plan type selector — hidden once plan is locked */}
              {!planAlreadySet && (
                <div className="flex gap-1 p-1 bg-gray-100 rounded-full w-fit mb-6">
                  <button
                    onClick={() => { setPaymentPlanType('full'); setPayoffRemaining(false); }}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-150 ${paymentPlanType === 'full' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Pay in Full
                  </button>
                  <button
                    onClick={() => setPaymentPlanType('installment')}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-150 ${paymentPlanType === 'installment' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    3-Month Installment
                  </button>
                </div>
              )}

              {/* Installment panel */}
              {paymentPlanType === 'installment' && (
                <div className="mb-6 space-y-4">
                  {!planAlreadySet && (
                    <div className="flex gap-2.5 items-start bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                      <FiAlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <p>Installment plans include a <span className="font-semibold">3% financing charge</span> spread across 3 monthly payments.</p>
                    </div>
                  )}

                  {/* Payment breakdown */}
                  {!planAlreadySet && balanceDue > 0 && (() => {
                    const fee = Math.round(balanceDue * 0.03);
                    const total = balanceDue + fee;
                    const perInstallment = Math.ceil(total / 3);
                    return (
                      <div className="rounded-xl border border-indigo-100 overflow-hidden">
                        <div className="px-4 py-2.5 bg-indigo-50 border-b border-indigo-100">
                          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Payment Breakdown</p>
                        </div>
                        <div className="divide-y divide-gray-100 text-sm">
                          <div className="flex justify-between px-4 py-3">
                            <span className="text-gray-500">Balance Due</span>
                            <span className="font-medium text-gray-800">NGN {Number(balanceDue).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between px-4 py-3">
                            <span className="text-gray-500">Financing Fee (3%)</span>
                            <span className="font-medium text-amber-600">+ NGN {Number(fee).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between px-4 py-3 bg-gray-50">
                            <span className="font-semibold text-gray-700">Total Financed</span>
                            <span className="font-bold text-gray-900">NGN {Number(total).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between px-4 py-3 bg-indigo-50">
                            <span className="font-semibold text-indigo-700">Per Installment <span className="font-normal text-indigo-400">(×3 months)</span></span>
                            <span className="font-bold text-indigo-700">NGN {Number(perInstallment).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {planAlreadySet && embeddedPlan && (
                    <div className="rounded-xl border border-indigo-100 overflow-hidden">
                      <div className="px-4 py-2.5 bg-indigo-50 border-b border-indigo-100">
                        <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Payment Breakdown</p>
                      </div>
                      <div className="divide-y divide-gray-100 text-sm">
                        <div className="flex justify-between px-4 py-3">
                          <span className="text-gray-500">Original Amount</span>
                          <span className="font-medium text-gray-800">NGN {Number(embeddedPlan.original_amount).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between px-4 py-3">
                          <span className="text-gray-500">Financing Fee ({Math.round((embeddedPlan.financing_rate || 0.03) * 100)}%)</span>
                          <span className="font-medium text-amber-600">
                            + NGN {Number(embeddedPlan.financed_amount - embeddedPlan.original_amount).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between px-4 py-3 bg-gray-50">
                          <span className="font-semibold text-gray-700">Total Financed</span>
                          <span className="font-bold text-gray-900">NGN {Number(embeddedPlan.financed_amount).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between px-4 py-3 bg-indigo-50">
                          <span className="font-semibold text-indigo-700">Per Installment <span className="font-normal text-indigo-400">(×{embeddedPlan.total_installments} months)</span></span>
                          <span className="font-bold text-indigo-700">NGN {Number(embeddedPlan.installment_amount).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {planLoading ? (
                    <div className="flex justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
                    </div>
                  ) : paymentPlan?.installments?.length > 0 ? (
                    <InstallmentTable
                      installments={paymentPlan.installments}
                      totalInstallments={paymentPlan.total_installments}
                      highlightNumber={paymentPlan.next_installment?.installment_number}
                      payoffRemaining={payoffRemaining}
                    />
                  ) : null}

                  {paymentPlan?.remaining_amount > 0 && paymentPlan?.pending_count > 1 && (
                    <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={payoffRemaining}
                        onChange={(e) => setPayoffRemaining(e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700">Pay off remaining balance</span>
                        <span className="text-xs text-gray-500 ml-1.5">
                          (NGN {Number(paymentPlan.remaining_amount).toLocaleString()} total)
                        </span>
                      </div>
                    </label>
                  )}
                </div>
              )}

              {/* Saved card */}
              {hasSavedCard && (
                <label className="flex items-center gap-3 mb-5 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useSavedCard}
                    onChange={(e) => setUseSavedCard(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-5 bg-gray-200 rounded flex items-center justify-center">
                      <FiCreditCard className="w-3.5 h-3.5 text-gray-500" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">Use saved card</span>
                    {savedAuth?.bank && (
                      <span className="text-xs text-gray-400">
                        {savedAuth.bank} ···· {savedAuth.last4}
                      </span>
                    )}
                  </div>
                </label>
              )}

              {/* Partial payment notice */}
              {hasPartialPayment && !planAlreadySet && (
                <div className="flex gap-2.5 items-start bg-blue-50 border border-blue-100 rounded-xl p-3 mb-5 text-sm text-blue-800">
                  <FiCheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p>
                    A partial payment of <span className="font-semibold">NGN {Number(subscription.total_paid).toLocaleString()}</span> has been recorded. Your remaining balance of <span className="font-semibold">NGN {Number(balanceDue).toLocaleString()}</span> will be charged.
                  </p>
                </div>
              )}

              {/* Pay button */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
                <button
                  onClick={handlePay}
                  disabled={payLoading}
                  className={`inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full font-semibold text-white text-sm transition-all duration-200 shadow-md ${
                    payLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#3F3DA5] to-[#3371E8] hover:from-[#2a2a7a] hover:to-[#2a5bb8] hover:shadow-lg hover:scale-[1.02]'
                  }`}
                >
                  {payLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FiCreditCard className="w-4 h-4" />
                      Pay Now {buttonAmount > 0 && `· NGN ${Number(buttonAmount).toLocaleString()}`}
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-400">🔒 Secured by Paystack · PCI DSS Compliant</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 text-center">
              <FiCreditCard className="w-10 h-10 text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">Contact your school administrator for billing matters.</p>
            </div>
          )}
        </SectionCard>
      )}

      {/* ── Subscription History ──────────────────────────────────────── */}
      <SectionCard title="Subscription History">
        {historyLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-10">
            <FiClock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No subscription history yet.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto -mx-6 -mt-6">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left font-medium">Term / Session</th>
                    <th className="px-6 py-3 text-left font-medium">Amount</th>
                    <th className="px-6 py-3 text-left font-medium">Status</th>
                    <th className="px-6 py-3 text-left font-medium hidden sm:table-cell">Paid</th>
                    <th className="px-6 py-3 text-left font-medium hidden md:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((sub) => {
                    const s = statusConfig[sub.status] || statusConfig.pending;
                    return (
                      <tr
                        key={sub._id}
                        onClick={() => setSelectedSub(sub)}
                        className="cursor-pointer hover:bg-[#F5F9FF] transition-colors"
                      >
                        <td className="px-6 py-3.5 font-medium text-gray-900 dark:text-gray-200">
                          {displaySchoolTerm(sub.term)} — {sub.session}
                        </td>
                        <td className="px-6 py-3.5 text-gray-700">
                          {sub.currency} {Number(sub.amount).toLocaleString()}
                        </td>
                        <td className="px-6 py-3.5">
                          <span className={`${s.bg} ${s.text} inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full`}>
                            {s.icon}
                            {s.label}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-gray-700 hidden sm:table-cell">
                          {sub.currency} {Number(sub.total_paid).toLocaleString()}
                        </td>
                        <td className="px-6 py-3.5 text-gray-400 text-xs hidden md:table-cell">
                          {new Date(sub.created_at).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {historyTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400">Page {historyPage} of {historyTotalPages}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchHistory(historyPage - 1)}
                    disabled={historyPage <= 1}
                    className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => fetchHistory(historyPage + 1)}
                    disabled={historyPage >= historyTotalPages}
                    className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* ── Subscription Detail Modal ─────────────────────────────────── */}
      {selectedSub && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

            {/* Modal header */}
            <div className={`px-6 py-5 rounded-t-2xl ${(statusConfig[selectedSub.status] || statusConfig.pending).headerBg}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {displaySchoolTerm(selectedSub.term)} — {selectedSub.session}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/20 text-white mt-2">
                    {(statusConfig[selectedSub.status] || statusConfig.pending).icon}
                    {(statusConfig[selectedSub.status] || statusConfig.pending).label}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedSub(null)}
                  className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                >
                  <FiX className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 bg-[#EEEEFC] divide-x divide-indigo-100">
              <div className="px-5 py-3">
                <p className="text-xs text-gray-500 mb-0.5">Total</p>
                <p className="text-base font-bold text-gray-800">{selectedSub.currency} {Number(selectedSub.amount).toLocaleString()}</p>
              </div>
              <div className="px-5 py-3">
                <p className="text-xs text-gray-500 mb-0.5">Paid</p>
                <p className="text-base font-bold text-green-600">{selectedSub.currency} {Number(selectedSub.total_paid).toLocaleString()}</p>
              </div>
            </div>

            {/* Details body */}
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm bg-gray-50 rounded-xl p-4">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Billing Date</p>
                  <p className="font-medium text-gray-700">
                    {selectedSub.billing_date
                      ? new Date(selectedSub.billing_date).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' })
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Billing Cycle</p>
                  <p className="font-medium text-gray-700 capitalize">{selectedSub.billing_cycle || '—'}</p>
                </div>
              </div>

              {/* Payments */}
              {selectedSub.payments?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">Payments</h4>
                  <div className="space-y-2">
                    {selectedSub.payments.map((payment, index) => (
                      <div key={payment._id || index} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-900 text-sm">
                            {selectedSub.currency} {Number(payment.amount).toLocaleString()}
                          </span>
                          <span className="text-xs text-gray-400">
                            {payment.paid_at
                              ? new Date(payment.paid_at).toLocaleString('en-NG', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : '—'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                          <div><span className="text-gray-400">Ref: </span><span className="font-mono">{payment.reference}</span></div>
                          <div><span className="text-gray-400">Method: </span><span className="capitalize">{payment.method || '—'}</span></div>
                          {payment.metadata?.last4 && (
                            <div><span className="text-gray-400">Card: </span><span>{payment.metadata.bank || ''} ···· {payment.metadata.last4}</span></div>
                          )}
                          {payment.metadata?.installment_number && (
                            <div><span className="text-gray-400">Installment: </span><span>#{payment.metadata.installment_number}</span></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status History */}
              {selectedSub.status_history?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">Status History</h4>
                  <div className="relative pl-5 border-l-2 border-gray-100 space-y-4">
                    {selectedSub.status_history.map((entry, index) => {
                      const ec = statusConfig[entry.status] || { bg: 'bg-gray-100', text: 'text-gray-700' };
                      return (
                        <div key={index} className="relative">
                          <div className="absolute -left-[21px] w-3 h-3 rounded-full bg-white border-2 border-gray-300" />
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`${ec.bg} ${ec.text} text-xs font-semibold px-2 py-0.5 rounded-full capitalize`}>
                              {entry.status}
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(entry.timestamp).toLocaleString('en-NG', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">{entry.reason}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
